<template>
  <div class="space-y-6">
    <SurveyCsvExportsPanel
      :survey-list="surveyList"
      :refresh-key="csvExportsRefreshKey"
    />

    <!-- RECENT REPORTS -->
    <div>
      <h2 class="font-bold mb-2">Recent Reports</h2>

      <div v-if="recentReports.length === 0" class="text-gray-500">
        No reports yet.
      </div>

      <ul class="space-y-1" v-else>
        <li
          v-for="report in recentReports"
          :key="report.uuid"
          class="p-2 border rounded hover:bg-gray-100 flex justify-between gap-2"
          @click="loadExistingReport(report.uuid)"
        >
          <div class="cursor-pointer flex-1">
            <div class="font-semibold">
              {{ getSurveyName(report.survey_uuid) }}
            </div>
            <div class="text-xs text-gray-500">
              Date: {{ formatDate(report.start_date) }} → {{ formatDate(report.end_date) }}
            </div>
            <div class="text-xs text-gray-500">
              Games: {{ formatGamesFilter(report.filter_place_ids) }}
            </div>
          </div>

          <div class="text-sm flex items-center gap-3">
            <span
              :class="{
                'text-green-600': report.status === 'completed',
                'text-yellow-600': report.status === 'running',
                'text-gray-600': report.status === 'pending',
                'text-red-600': report.status === 'failed',
              }"
            >
              {{ report.status }}
            </span>

            <button
              type="button"
              class="text-gray-400 hover:text-green-600 disabled:opacity-50"
              title="Queue raw CSV export"
              :disabled="queuingCsvExportReportUuid === report.uuid"
              @click.stop="queueCsvExportForReport(report)"
            >
              ⬇
            </button>

            <button
              type="button"
              class="text-gray-400 hover:text-red-600 disabled:opacity-50"
              title="Delete report"
              :disabled="deletingReportUuid === report.uuid"
              @click.stop="confirmDeleteReport(report)"
            >
              🗑
            </button>
          </div>
        </li>
      </ul>

      <div
        v-if="reportToDelete"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      >
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-semibold mb-2">Delete survey report?</h3>

          <p class="text-sm text-gray-600 mb-4">
            This will permanently delete the report for
            <span class="font-semibold">{{ getSurveyName(reportToDelete.survey_uuid) }}</span>
            from {{ formatDate(reportToDelete.start_date) }} to {{ formatDate(reportToDelete.end_date) }}.
          </p>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="px-3 py-1 border rounded"
              :disabled="deletingReportUuid"
              @click="reportToDelete = null"
            >
              Cancel
            </button>

            <button
              type="button"
              class="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50"
              :disabled="deletingReportUuid"
              @click="deleteReport"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="lastPage > 1"
        class="mt-2 flex items-center justify-between text-sm text-gray-700"
      >
        <div>
          Page {{ currentPage }} of {{ lastPage }} ({{ totalReports }} reports)
        </div>
        <div class="space-x-2">
          <button
            class="px-2 py-1 border rounded disabled:opacity-50"
            :disabled="currentPage <= 1"
            @click="changeReportPage(currentPage - 1)"
          >
            Prev
          </button>
          <button
            class="px-2 py-1 border rounded disabled:opacity-50"
            :disabled="currentPage >= lastPage"
            @click="changeReportPage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <hr />

    <!-- REPORT GENERATION CONTROLS -->
    <div>
      <label class="block mb-2">Select Survey:</label>

      <select v-model="selectedSurveyUuid" class="border p-2 rounded w-full">
        <option disabled value="">-- Select Survey --</option>

        <option
          v-for="survey in sortedSurveyList"
          :key="survey.uuid"
          :value="survey.uuid"
        >
          {{ survey.name }}
        </option>
      </select>
    </div>

    <div class="flex flex-col sm:flex-row sm:space-x-4">
      <div class="flex-1">
        <label class="block mb-2">Start Date:</label>
        <input type="date" v-model="startDate" class="border p-2 rounded w-full" />
      </div>

      <div class="flex-1">
        <label class="block mb-2">End Date:</label>
        <input type="date" v-model="endDate" class="border p-2 rounded w-full" />
      </div>
    </div>

    <!-- Survey Games -->
    <div v-if="selectedSurveyUuid">
      <label class="block mb-2">Game(s):</label>
      <DropdownComponent
        :items="all_survey_games"
        display="name"
        selected="Survey Games"
        @user_select="addGameToFilter"
        class="w-full"
      />
      <div class="flex flex-row flex-wrap gap-1 mt-2">
        <label
          v-for="game in game_filter_array"
          :key="game.uuid"
          class="bg-white rounded-md px-2 py-1 cursor-pointer hover:bg-blue-300"
          @click="removeGameFromFilter(game)"
        >
          {{ game.friendly_name || game.name }}
        </label>
      </div>
    </div>

    <div>
      <button @click="dispatchReport" class="bg-blue-500 text-white p-2 rounded">
        Generate Report
      </button>
    </div>

    <div v-if="loading" class="pt-4 text-center text-blue-600">
      Loading report...
    </div>

    <div v-if="errorMessage" class="pt-4 text-center text-red-600">
      {{ errorMessage }}
    </div>

    <!-- REPORT OUTPUT -->
    <div v-if="activeReport" class="mt-4">
      <h2 class="font-bold text-lg">
        Report Results
      </h2>

      <div class="mt-2 text-sm space-y-2">
        <div
          v-if="activeReport.status === 'failed' && activeReport.error_message"
          class="p-3 bg-red-100 border border-red-400 text-red-700 rounded"
        >
          <div class="font-semibold">Report failed</div>
          <div>{{ activeReport.error_message }}</div>
        </div>

        <div class="text-gray-700 space-y-1">
          <div>
            <span class="font-semibold">Survey:</span>
            {{ getSurveyName(activeReport.survey_uuid) }}
          </div>
          <div>
            <span class="font-semibold">Date Range:</span>
            {{ formatDate(activeReport.start_date) }} → {{ formatDate(activeReport.end_date) }}
          </div>
          <div>
            <span class="font-semibold">Games:</span>
            {{ formatGamesFilter(activeReport.filter_place_ids) }}
          </div>
          <div>
            <span class="font-semibold">Status:</span>
            {{ activeReport.status }}
          </div>
        </div>
      </div>

      <div class="mt-4 flex gap-2">
        <button
          v-if="reportRows.length"
          @click="exportCSV"
          class="bg-green-500 text-white p-2 rounded"
        >
          Export CSV
        </button>

        <button
          type="button"
          class="bg-green-700 text-white p-2 rounded disabled:opacity-50"
          :disabled="queuingCsvExportReportUuid === activeReport.uuid"
          @click="queueCsvExportForReport(activeReport)"
        >
          Queue Raw CSV
        </button>
      </div>

      <table v-if="reportRows.length" class="min-w-full border-collapse mt-4">
        <thead>
          <tr>
            <th class="border p-2">Question/Response</th>
            <th class="border p-2">Responses</th>
            <th class="border p-2">Percentage</th>
            <th class="border p-2">Average Time Spent On Question</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, i) in reportRows" :key="i">
            <td class="border p-2" :class="{ 'font-bold text-lg': row.is_question }">
              {{ row.label }}
            </td>
            <td class="border p-2 text-center">{{ row.responses_count }}</td>
            <td class="border p-2 text-center">
              {{ row.is_question ? '-' : formatPercentage(row.percentage) }}
            </td>
            <td class="border p-2 text-center">
              {{ this.formatSeconds(row.average_time_seconds) ?? "" }}
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="mt-4 text-sm text-gray-500">
        No row data available for this report.
      </div>
    </div>
  </div>
</template>

<script>
import SurveyCsvExportsPanel from "@/components/SurveyCsvExportsPanel.vue";

export default {
  name: "SurveyReportingOnQueue",

  components: {
    SurveyCsvExportsPanel,
  },

  data() {
    return {
      reportToDelete: null,
      deletingReportUuid: null,
      queuingCsvExportReportUuid: null,
      csvExportsRefreshKey: 0,
      surveyList: [],
      recentReports: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 5,
      totalReports: 0,

      selectedSurveyUuid: "",
      startDate: "",
      endDate: "",

      all_survey_games: [],
      game_filter_array: [],

      allGames: [],
      gamesByPlaceId: {},

      activeReportUuid: null,
      activeReport: null,
      reportRows: [],
      loading: false,
      errorMessage: "",

      pollingTimer: null,
      recentPollingTimer: null,
    };
  },

  created() {
    this.fetchSurveyList();
    this.fetchAllGames();
    this.fetchRecentReports();
    this.startRecentReportsPolling();
  },

  beforeUnmount() {
    clearInterval(this.pollingTimer);
    clearInterval(this.recentPollingTimer);
  },

  watch: {
    selectedSurveyUuid(newVal) {
      this.all_survey_games = [];
      this.game_filter_array = [];
      if (newVal) this.fetchSurveyGames();
    },
  },

  computed: {
    sortedSurveyList() {
      return [...this.surveyList].sort((a, b) => {
        const aDate = a.start_datetime || a.start_date || '';
        const bDate = b.start_datetime || b.start_date || '';

        return new Date(bDate) - new Date(aDate);
      });
    }
  },

  methods: {
    async fetchSurveyList() {
      try {
        const res = await this.$http.get("/surveys");
        this.surveyList = res.data;
        this.$notify({ type: "success", text: "Loaded survey list" });
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to load survey list" });
      }
    },

    async fetchSurveyGames() {
      try {
        const res = await this.$http.get(`/surveys/survey-games/${this.selectedSurveyUuid}`);
        const items = Array.isArray(res.data) ? res.data : [];
        this.all_survey_games = items.map(g => ({
          ...g,
          friendly_name: g.friendly_name || g.name,
        }));
        this.$notify({ type: "success", text: "Loaded survey games" });
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to load survey games" });
      }
    },

    async fetchAllGames() {
      try {
        const res = await this.$http.get("/games");
        const games = Array.isArray(res.data) ? res.data : [];

        this.allGames = games.map(g => ({
          ...g,
          friendly_name: g.friendly_name || g.name,
        }));

        const index = {};
        this.allGames.forEach(g => {
          if (g.place_id) {
            index[g.place_id] = g;
          }
        });
        this.gamesByPlaceId = index;

        this.$notify({ type: "success", text: "Loaded game lookup data" });
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to load game lookup data" });
      }
    },

    async fetchRecentReports() {
      try {
        const res = await this.$http.get("/survey-reports", {
          params: {
            per_page: this.perPage,
            page: this.currentPage,
          },
        });

        const payload = res.data;

        this.recentReports = payload.data || [];
        if (payload.meta) {
          this.currentPage   = payload.meta.current_page || 1;
          this.lastPage      = payload.meta.last_page || 1;
          this.perPage       = payload.meta.per_page || this.perPage;
          this.totalReports  = payload.meta.total || this.totalReports;
        }
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to load recent reports" });
      }
    },

    startRecentReportsPolling() {
      this.recentPollingTimer = setInterval(() => {
        this.fetchRecentReports();
      }, 10000);
    },

    changeReportPage(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      this.fetchRecentReports();
    },

    addGameToFilter(game) {
      if (!game || !game.uuid) return;

      const alreadySelected = this.game_filter_array.some(g => g.uuid === game.uuid);
      if (alreadySelected) return;

      this.game_filter_array.push(game);
      this.all_survey_games = this.all_survey_games.filter(el => el.uuid !== game.uuid);
    },

    removeGameFromFilter(game) {
      if (!game || !game.uuid) return;

      this.game_filter_array = this.game_filter_array.filter(g => g.uuid !== game.uuid);
      this.all_survey_games.push(game);
      this.all_survey_games.sort((a, b) => {
        const nameA = (a.name || a.friendly_name || "").toLowerCase();
        const nameB = (b.name || b.friendly_name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },

    async dispatchReport() {
      if (!this.selectedSurveyUuid) {
        const text = "Select a survey first.";
        this.errorMessage = text;
        this.$notify({ type: "error", text });
        return;
      }

      this.errorMessage = "";
      this.loading = true;

      const params = {
        survey_uuid: this.selectedSurveyUuid,
        start_date: this.startDate || null,
        end_date: this.endDate || null,
        game_place_ids: this.game_filter_array.map((g) => g.place_id),
      };

      try {
        const { data } = await this.$http.post("/survey-reports/generate", params);

        if (data.status === "completed" && data.report_uuid) {
          this.$notify({ type: "success", text: "Report generated, loading results" });
          this.loadExistingReport(data.report_uuid);
          return;
        }

        if (data.fingerprint) {
          this.$notify({ type: "success", text: "Report queued, it will appear in the list shortly" });
          this.pollForReport(data.fingerprint);
        } else {
          this.$notify({ type: "error", text: "Unexpected response when queuing report" });
        }

      } catch (e) {
        const text = "Failed to queue report.";
        this.errorMessage = text;
        this.$notify({ type: "error", text });
      } finally {
        this.loading = false;
      }
    },

    async queueCsvExportForReport(report) {
      if (!report || !report.survey_uuid) return;

      this.queuingCsvExportReportUuid = report.uuid;

      try {
        await this.$http.post("/survey-csv-exports", {
          survey_uuid: report.survey_uuid,
          start_date: this.normaliseExportDate(report.start_date),
          end_date: this.normaliseExportDate(report.end_date),
        });

        this.csvExportsRefreshKey++;

        this.$notify({ type: "success", text: "CSV export queued" });
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to queue CSV export" });
      } finally {
        this.queuingCsvExportReportUuid = null;
      }
    },

    normaliseExportDate(value) {
      if (!value || value === "N/A") return null;

      const date = String(value).substring(0, 10);

      return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
    },

    pollForReport(fingerprint) {
      clearInterval(this.pollingTimer);

      this.pollingTimer = setInterval(async () => {
        try {
          const res = await this.$http.get(`/survey-reports/by-fingerprint/${fingerprint}`);

          if (!res.data || !res.data.report) return;

          const r = res.data.report;

          if (r.status === "completed") {
            clearInterval(this.pollingTimer);
            this.$notify({ type: "success", text: "Report completed" });
            this.loadExistingReport(r.uuid);
          }

          if (r.status === "failed") {
            clearInterval(this.pollingTimer);
            this.activeReport = r;
            this.reportRows = [];
            const text = r.error_message
              ? `Report failed: ${r.error_message}`
              : "Report failed to generate.";
            this.errorMessage = text;
            this.$notify({ type: "error", text });
          }
        } catch (e) {
          this.$notify({ type: "error", text: "Error while polling report status" });
          clearInterval(this.pollingTimer);
        }
      }, 3000);
    },

    async loadExistingReport(uuid) {
      this.loading = true;
      this.errorMessage = "";
      this.reportRows = [];
      this.activeReport = null;

      try {
        const res = await this.$http.get(`/survey-reports/${uuid}`);
        this.activeReportUuid = uuid;
        this.activeReport = res.data.report || null;
        this.reportRows = res.data.rows || [];
      } catch (e) {
        const text = "Failed to load stored report.";
        this.errorMessage = text;
        this.$notify({ type: "error", text });
      } finally {
        this.loading = false;
      }
    },

    exportCSV() {
      if (!this.reportRows.length) return;

      const headers = [
        "Question/Response",
        "Responses",
        "Percentage",
        "Average Time Spent On Question",
      ];

      let csv = headers.join(",") + "\n";

      this.reportRows.forEach((r) => {
        csv += [
          `"${r.label.replace(/"/g, '""')}"`,
          r.responses_count,
          r.is_question ? "-" : r.percentage + "%",
          r.average_time_seconds ?? "",
        ].join(",") + "\n";
      });

      const baseName = this.activeReport
        ? `SurveyReport-${this.getSurveyName(this.activeReport.survey_uuid)}-${this.formatDate(this.activeReport.start_date)}-${this.formatDate(this.activeReport.end_date)}`
        : `SurveyReport-${this.activeReportUuid}`;

      const filename = `${baseName}.csv`.replace(/\s+/g, "-");

      const link = document.createElement("a");
      link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
      link.download = filename;
      link.click();

      this.$notify({ type: "success", text: "Exported report CSV" });
    },

    formatSeconds(value) {
      if (value === null || value === undefined) return '';
      const num = Number(value);
      if (Number.isNaN(num)) return '';
      return num.toFixed(1);
    },

    formatPercentage(value) {
      if (value === null || value === undefined) return '';
      const num = Number(value);
      if (Number.isNaN(num)) return '';
      return num.toFixed(1) + '%';
    },

    confirmDeleteReport(report) {
      this.reportToDelete = report;
    },

    async deleteReport() {
      if (!this.reportToDelete) return;

      const uuid = this.reportToDelete.uuid;
      this.deletingReportUuid = uuid;

      try {
        await this.$http.delete(`/survey-reports/${uuid}`);

        if (this.activeReportUuid === uuid) {
          this.activeReportUuid = null;
          this.activeReport = null;
          this.reportRows = [];
        }

        this.reportToDelete = null;
        await this.fetchRecentReports();

        this.$notify({ type: "success", text: "Survey report deleted" });
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to delete survey report" });
      } finally {
        this.deletingReportUuid = null;
      }
    },

    getSurveyName(uuid) {
      const survey = this.surveyList.find((s) => s.uuid === uuid);
      return survey ? survey.name : uuid;
    },

    formatDate(value) {
      if (!value) return "N/A";
      return String(value).substring(0, 10);
    },

    formatGamesFilter(filterPlaceIds) {
      if (!filterPlaceIds) return "All games";

      let ids;
      if (Array.isArray(filterPlaceIds)) {
        ids = filterPlaceIds;
      } else if (typeof filterPlaceIds === "object") {
        ids = Object.values(filterPlaceIds);
      } else {
        ids = [];
      }

      const names = [];
      const seen = new Set();

      ids.forEach(id => {
        if (!id) return;
        const game = this.gamesByPlaceId[id];
        if (!game) return;

        const name = game.friendly_name || game.name;
        if (!name || seen.has(name)) return;

        seen.add(name);
        names.push(name);
      });

      if (!names.length) return "All games";
      if (names.length <= 3) return names.join(", ");
      return `${names.slice(0, 3).join(", ")} (+${names.length - 3} more)`;
    },
  },
};
</script>